import React, { useState } from "react";
import {
  ReservationColumnKey,
  SettlementDataTable,
} from "../components/SettlementDataTable";
import { PreCheckStatus } from "../types/settlementBooking";
import MultiSelect from "@/components/common/MultiSelect";
import { Button } from "@/components/ui/button";
import {
  Download,
  Plane,
  Smartphone,
  BrushCleaning,
  ClipboardList,
  MessageSquare,
  SquareUserRound,
  UserPlus,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { exportSettlementBookingsToCSV } from "@/components/pages/settlement/utils/exportSettlementBookingsToCSV";
import { useArrivals } from "@/components/pages/settlement/hooks/useSettlement";
import { useBookingSources } from "@/components/pages/settlement/hooks/useBookingSources";
import { useHotel } from "@/hooks/useHotel";
import { format, startOfDay } from "date-fns";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { useSettlementSelectedOptions } from "../hooks/useSettlementSelectedOptions";
import { InteractiveStatusCell } from "../components/table-cells/InteractiveStatusCell";
import { SettlementFiltersBar } from "../components/SettlementFiltersBar";
import { SettlementSummaryBar } from "../components/SettlementSummaryBar";
import { StatCard } from "../components/StatCard";
import { getSafeValue } from "@/components/pages/settlement/utils/getSafeValue";
import { CheckinStatus } from "@/types/booking";
import { useTranslation } from "react-i18next";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useCreateChannelWithGuest } from "@/components/pages/chats/hooks/useChannel";
import { useNavigate } from "react-router-dom";

const arrivalsColumns: ReservationColumnKey[] = [
  "select",
  "mainGuest",
  "guests",
  "room",
  "roomService",
  "arrivalDate",
  "departureDate",
  "preCheckIn",
  "documentsVerified",
  "requests",
  "balance",
  "checkInCompleted",
  "actions",
];

const perPage = 10;

export const ArrivalsPage: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [arrivalDate, setArrivalDate] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: startOfDay(new Date()),
    to: startOfDay(new Date()),
  });
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const [preCheckinStatus, setPreCheckinStatus] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);

  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();
  const navigate = useNavigate();
  const { mutateAsync: createChannelWithGuest, isPending: isCreatingChannel } =
    useCreateChannelWithGuest();

  const { reservations, loading, totalPages } = useArrivals(
    {
      currentPage,
      perPage,
      arrivalDate: format(arrivalDate.from ?? new Date(), "yyyy-MM-dd"),
      arrivalDateTo: format(arrivalDate.to ?? new Date(), "yyyy-MM-dd"),
      sortField,
      sortDirection,
      preCheckinStatus,
      channels,
    },
    hotelId,
  );
  const summary = undefined;

  const { handleChangeStatus, ...rest } = useSettlementSelectedOptions({
    reservations,
    mode: "checkin",
  });

  const { data: bookingSources } = useBookingSources(hotelId);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => {
        if (prev === "asc") return "desc";
        return "asc";
      });
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const hasSelection = rest?.selectedCount > 0;
  const selectedReservations = reservations.filter((reservation) =>
    rest.selectedItems.includes(reservation.id),
  );
  const firstSelectedReservation = selectedReservations[0];
  const hasConfirmableSelection = selectedReservations.some(
    (reservation) => reservation.checkinStatus !== CheckinStatus.DONE,
  );
  const visibleColumns = capabilities.canUseRequestsSystem
    ? arrivalsColumns
    : arrivalsColumns.filter(
        (column) => column !== "roomService" && column !== "requests",
      );

  const handleCSVExport = () => {
    const selectedIds = rest.selectedItems;
    const dataToExport =
      selectedIds.length > 0
        ? reservations.filter((r) => selectedIds.includes(r.id))
        : reservations;

    exportSettlementBookingsToCSV(
      dataToExport,
      visibleColumns,
      t,
      "arrivals-export.csv",
    );
  };

  const handleSendMessage = async () => {
    if (!firstSelectedReservation) {
      return;
    }

    const channel = await createChannelWithGuest(firstSelectedReservation.id);

    if (!channel.id) {
      return;
    }

    navigate(`/chats?activeChannel=${encodeURIComponent(channel.id)}`);
  };

  const handleSendConfirmation = () => {
    if (!hasConfirmableSelection || rest.isBulkUpdating) {
      return;
    }

    handleChangeStatus(CheckinStatus.DONE);
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-4 p-12">
      <SettlementSummaryBar>
        <StatCard
          icon={<Plane className="text-light-stone size-6" />}
          label={t("reservation.statusCard.totalArrivals")}
          value={getSafeValue(summary?.total)}
        />
        <StatCard
          icon={<Smartphone className="text-light-stone size-6" />}
          label={t("reservation.statusCard.checkedInOnline")}
          value={getSafeValue(summary?.preCheckInsPercentage)}
        />
        {capabilities.canUseRequestsSystem ? (
          <>
            <StatCard
              icon={<BrushCleaning className="text-light-stone size-6" />}
              label={t("reservation.table.roomService")}
              value={`${getSafeValue(summary?.roomServicesDoneCount)}/${getSafeValue(summary?.roomServicesCount)}`}
            />
            <StatCard
              icon={<ClipboardList className="text-light-stone size-6" />}
              label={t("reservation.table.requests")}
              value={`${getSafeValue(summary?.serviceRequestsDoneCount)}/${getSafeValue(summary?.serviceRequestsCount)}`}
            />
          </>
        ) : null}
      </SettlementSummaryBar>

      <div className={"flex flex-col gap-6"}>
        <div
          className={cn(
            "flex items-center justify-between",
            hasSelection && "bg-neutral-50 px-6 py-4",
          )}
        >
          {hasSelection ? (
            <span className="text-sm font-medium text-neutral-400">
              {rest.selectedCount} selected
            </span>
          ) : (
            <SettlementFiltersBar>
              <DateRangePicker
                dateRange={arrivalDate}
                onDateRangeChange={setArrivalDate}
                className="text-foreground flex h-8 min-h-8 items-center gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
              />
              <MultiSelect
                options={[
                  { label: "Done", value: PreCheckStatus.DONE },
                  { label: "Sent 1 day", value: PreCheckStatus.SENT },
                  { label: "Sent 3 day", value: PreCheckStatus.SENT_3 },
                  { label: "Sent 7 day", value: PreCheckStatus.SENT_7 },
                  { label: "Not done", value: PreCheckStatus.NOT_DONE },
                  { label: "Not send", value: PreCheckStatus.NOT_SEND },
                ]}
                placeholder={"Status"}
                value={preCheckinStatus}
                onValueChange={setPreCheckinStatus}
                className="text-foreground min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                dropdownClassName="border border-slate-200 border-t-0"
              />
              <MultiSelect
                className={
                  "text-foreground min-h-8 min-w-[110px] gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                }
                options={
                  bookingSources?.map((bs) => ({
                    label: bs.toLowerCase(),
                    value: bs,
                  })) ?? []
                }
                placeholder={"Chanel"}
                value={channels}
                onValueChange={setChannels}
                dropdownClassName="border border-slate-200 border-t-0"
              />
            </SettlementFiltersBar>
          )}

          <div className="flex items-center gap-x-2">
            {hasSelection && (
              <>
                <InteractiveStatusCell
                  statuses={[
                    {
                      status: CheckinStatus.DONE,
                      color: "border-accent-green text-accent-green",
                      text: t("reservation.settlement.confirm"),
                    },
                    {
                      status: CheckinStatus.NOT_DONE,
                      color: "border-gray-5 text-gray-5",
                      text: t("reservation.timeline.checkIn"),
                    },
                  ]}
                  currentStatus={CheckinStatus.NOT_DONE}
                  onStatusChange={(newStatus) =>
                    handleChangeStatus(newStatus as CheckinStatus)
                  }
                  className="text-foreground h-8 flex-row-reverse rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm font-medium hover:opacity-100"
                  icon={<SquareUserRound className="size-4" />}
                />

                <Button
                  variant="icon-text"
                  onClick={handleSendMessage}
                  disabled={!firstSelectedReservation || isCreatingChannel}
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
                >
                  <MessageSquare className="size-4" />
                  {t("reservation.buttons.sendMessage")}
                </Button>

                <Button
                  variant="icon-text"
                  onClick={handleSendConfirmation}
                  disabled={!hasConfirmableSelection || rest.isBulkUpdating}
                  className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
                >
                  <UserPlus className="size-4" />
                  {t("reservation.buttons.sendConfirmation")}
                </Button>
              </>
            )}

            <Button
              variant="icon-text"
              onClick={handleCSVExport}
              className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
            >
              <Download className="size-4" />
              {t("reservation.buttons.export")}
            </Button>

            {hasSelection && (
              <Button
                variant="icon-text"
                onClick={rest.clearSelection}
                className="h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium text-red-500 shadow-xs"
              >
                <X className="size-4" />
                {t("reservation.buttons.cancel")}
              </Button>
            )}
          </div>
        </div>

        <SettlementDataTable
          data={reservations}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          loading={loading}
          {...rest}
        />
      </div>
    </div>
  );
};
